var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import ClaimTypeTile from '@backoffice/components/claim/ClaimTypeTile.vue';
import { fetchResourceCollection } from '@backoffice/utils/api';
import departementsData from '@shared/counting/departements.json';
import { ApiResourceName } from '@shared/types/api/api';
import { HousingTypeSlug } from '@shared/types/api/contract';
import { apiResourcePath } from '@shared/utils/apiResourcePath';
import { INSURER_CONFIG } from '@shared/insurer';
import { uuidFromIri } from '@shared/utils/iri';
import { Step } from '@shared/claim/steps';
import { isAxiosError } from '@shared/utils/axios';
const claimTypes = namespace('claimTypes');
const HOUSING_TYPE_OPTIONS = [
    {
        value: HousingTypeSlug.House,
        image: require('@backoffice/assets/pictures/housing-type/house.svg'),
    },
    {
        value: HousingTypeSlug.Apartment,
        image: require('@backoffice/assets/pictures/housing-type/apartment.svg'),
    },
];
let CreateCountingModal = class CreateCountingModal extends Vue {
    constructor() {
        super(...arguments);
        // Modal state
        this.isFindingUser = false;
        this.isFindingContracts = false;
        this.isCreatingClaim = false;
        // Form data
        this.departmentSearchInput = '';
        this.departmentInput = null;
        this.selectedUser = null;
        this.selectedHousingType = null;
        this.selectedContract = null;
        this.selectedClaimType = null;
        this.caseNumberInput = '';
    }
    get isModalOpen() {
        return this.open;
    }
    set isModalOpen(opened) {
        this.$emit('update:open', opened);
    }
    get selectedDepartment() {
        return departementsData.find((d) => d.code === this.departmentInput) || null;
    }
    set selectedDepartment(department) {
        var _a;
        this.departmentInput = (_a = department === null || department === void 0 ? void 0 : department.code) !== null && _a !== void 0 ? _a : null;
    }
    get isValid() {
        return !!(this.caseNumberInput &&
            this.selectedUser &&
            this.selectedContract &&
            this.selectedClaimType);
    }
    // Form options
    get departmentsOptions() {
        return !this.departmentSearchInput
            ? departementsData
            : departementsData.filter(({ code, nom }) => {
                const lowerInput = this.departmentSearchInput.toLowerCase();
                return [code.toLowerCase(), nom.toLowerCase()].some((str) => str.includes(lowerInput));
            });
    }
    get housingTypeOptions() {
        return HOUSING_TYPE_OPTIONS;
    }
    get claimTypeOptions() {
        return this.availableClaimTypes.filter((t) => !t.hidden);
    }
    // Async handlers
    async findUser() {
        try {
            this.isFindingUser = true;
            this.selectedUser = await fetchResourceCollection(ApiResourceName.Users, this.$axios, {
                email: 'john.doe@claims-ia.tech',
            }).then(({ data }) => data[0] || null);
        }
        finally {
            this.isFindingUser = false;
        }
    }
    async findContract() {
        var _a;
        if (!this.selectedUser || !this.selectedDepartment || !this.selectedHousingType)
            return;
        try {
            this.isFindingContracts = true;
            this.selectedContract = await fetchResourceCollection(ApiResourceName.Contracts, this.$axios, {
                user: this.selectedUser.id,
                'department.id': this.selectedDepartment.code,
                'housingType.slug': (_a = this.selectedHousingType) === null || _a === void 0 ? void 0 : _a.value,
            }).then(({ data }) => {
                var _a;
                return (_a = data.find(
                // On force le type primitif string en faisant de la concaténation, sinon les deux champs
                // ne sont pas considérés égaux à cause du typage de selectedHousingType (il semblerait)
                (contract) => { var _a; return '' + contract.housingType.slug === '' + ((_a = this.selectedHousingType) === null || _a === void 0 ? void 0 : _a.value); })) !== null && _a !== void 0 ? _a : data[0];
            });
        }
        finally {
            this.isFindingContracts = false;
        }
    }
    onDepartmentSelected(input) {
        if (input) {
            this.selectedDepartment = input;
            this.findContract();
        }
        else {
            this.selectedDepartment = null;
            this.selectedContract = null;
        }
    }
    onHousingTypeSelected(input) {
        if (input) {
            this.selectedHousingType = input;
            this.findContract();
        }
        else {
            this.selectedHousingType = null;
            this.selectedContract = null;
        }
    }
    clearForm() {
        this.departmentSearchInput = '';
        this.caseNumberInput = '';
        this.selectedDepartment = null;
        this.selectedHousingType = null;
        this.selectedContract = null;
        this.selectedClaimType = null;
        this.isCreatingClaim = false;
        this.isFindingContracts = false;
        this.isFindingUser = false;
    }
    resetForm() {
        this.clearForm();
        this.$emit('update:open', false);
    }
    async submit() {
        var _a, _b, _c;
        if (!this.selectedUser ||
            !this.selectedContract ||
            !this.selectedClaimType ||
            !this.caseNumberInput.length) {
            this.$buefy.toast.open({
                message: 'Une donnée nécessaire est manquante',
                type: 'is-danger',
            });
            return;
        }
        const payload = {
            caseNumber: this.caseNumberInput,
            policyholder: this.selectedUser.id,
            contract: this.selectedContract.id,
            type: this.selectedClaimType.id,
            entity: (_a = this.$auth.user) === null || _a === void 0 ? void 0 : _a.entity,
        };
        try {
            this.isCreatingClaim = true;
            const claim = await this.$axios.$post(apiResourcePath(ApiResourceName.Claims), payload);
            await this.$axios.$post(apiResourcePath(ApiResourceName.Countings), {
                title: claim.caseNumber,
                claim: claim.id,
            });
            await this.$axios.$put(`/claims-brms/${uuidFromIri(claim.id)}`, {
                step: Step.ClaimType,
                type: this.selectedClaimType.id,
                isCreationFromBackoffice: true,
            });
            const routeId = ((_b = INSURER_CONFIG.backoffice) === null || _b === void 0 ? void 0 : _b.claimUrlIdentifier)
                ? claim[INSURER_CONFIG.backoffice.claimUrlIdentifier]
                : uuidFromIri(claim.id);
            this.resetForm();
            this.$goToLocalePath({ name: 'chiffrage-id-declaration', params: { id: routeId } });
        }
        catch (err) {
            let message;
            // If the error is an HTTP error with a body, try to get a specific error message key
            if (isAxiosError(err) && ((_c = err.response) === null || _c === void 0 ? void 0 : _c.data)) {
                // Try to get the error key from the response body
                const errorData = err.response.data;
                const errorKey = errorData['hydra:description'];
                // If there is a description and a corresponding translation, use it
                if (errorKey && this.$te(`error.${errorKey}`))
                    message = this.$tc(`error.${errorKey}`);
            }
            this.$buefy.toast.open({
                message: message !== null && message !== void 0 ? message : this.$tc('error.default'),
                type: 'is-danger',
            });
        }
        finally {
            this.isCreatingClaim = false;
        }
    }
};
__decorate([
    claimTypes.Getter('availableClaimTypes')
], CreateCountingModal.prototype, "availableClaimTypes", void 0);
CreateCountingModal = __decorate([
    Component({
        middleware: ['departments'],
        async fetch() {
            await this.findUser();
            await this.$store.dispatch('claimTypes/loadClaimTypes');
        },
        components: { ClaimTypeTile },
        props: {
            open: {
                type: Boolean,
                required: true,
            },
        },
    })
], CreateCountingModal);
export default CreateCountingModal;
